import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  FC,
  ReactNode,
} from 'react';

export type FormOutageNoticeStatus =
  | 'neutral'
  | 'positive'
  | 'info'
  | 'warning'
  | 'prominent'
  | 'negative';

export interface FormOutageNotice {
  status: FormOutageNoticeStatus;
  title: string;
  description: string;
}

interface FormOutageNoticeContextValue {
  formOutageNotice: FormOutageNotice | null;
  requestNotice: () => void;
}

const FormOutageNoticeContext = createContext<FormOutageNoticeContextValue>({
  formOutageNotice: null,
  // Without a provider there's no notice to load.
  requestNotice: () => {
    return undefined;
  },
});

interface FormOutageNoticeProviderProps {
  children: ReactNode;
  formOutageNotice?: FormOutageNotice | null;
  // Prebuilt Node pages don't come with the notice, so the first form on the
  // page loads it from /api/form-outage-notice instead.
  loadInBrowser?: boolean;
}

export const FormOutageNoticeProvider: FC<FormOutageNoticeProviderProps> = ({
  children,
  formOutageNotice = null,
  loadInBrowser = false,
}) => {
  const [loadedNotice, setLoadedNotice] = useState<FormOutageNotice | null>(
    null,
  );
  const hasRequested = useRef(false);

  const requestNotice = useCallback(async () => {
    if (!loadInBrowser || hasRequested.current) {
      return;
    }
    hasRequested.current = true;
    try {
      const response = await fetch('/api/form-outage-notice/');
      setLoadedNotice(response.ok ? await response.json() : null);
    } catch {
      setLoadedNotice(null);
    }
  }, [loadInBrowser]);

  const value = useMemo(() => {
    return {
      formOutageNotice: formOutageNotice ?? loadedNotice,
      requestNotice,
    };
  }, [formOutageNotice, loadedNotice, requestNotice]);

  return (
    <FormOutageNoticeContext.Provider value={value}>
      {children}
    </FormOutageNoticeContext.Provider>
  );
};

export const useFormOutageNotice = () => {
  const { formOutageNotice, requestNotice } = useContext(
    FormOutageNoticeContext,
  );
  useEffect(() => {
    requestNotice();
  }, [requestNotice]);
  return { formOutageNotice };
};
